import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NextActionPayload, SALES_ACTIVITY_TYPES, TaskTypeEnum, taskTypeLabelKey } from 'lib-trend-core';
import { ScheduleMoment } from '../schedule-moment-field/schedule-moment-field.component';

/** The value of the form `buildForm` creates. */
export interface NextActivityFormValue {
  type: string;
  name?: string;
  moment: ScheduleMoment | null;
  description?: string;
}

/**
 * THE form that schedules an activity (SPEC-2026-10-crm-activities-cadence RF-01, RF-04, RF-16):
 * type, title, moment and notes. Rendered by the next-action dialog, the task form and the second
 * step of the conclusion dialog, so the seven sales types and the "sem horário" field are offered
 * the same way everywhere. The parent owns the `FormGroup` (built with `buildForm`) and reads it
 * with `toPayload`; this component only renders it.
 */
@Component({
  selector: 'app-next-activity-form',
  templateUrl: './next-activity-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class NextActivityFormComponent {

  @Input() form!: FormGroup;
  /** An older type the edited activity already carries, kept selectable beside the seven (RF-02). */
  @Input() extraTypes: Array<string> = [];
  @Input() allowUntimed = true;
  @Input() showNotes = true;
  @Input() idPrefix = 'nextActivity';

  /** The seven sales types, plus the edited activity's older type when it has one (RF-01, RF-02). */
  get types(): Array<{ value: string; label: string }> {
    const extra = (this.extraTypes ?? []).filter((type) => !!type && !SALES_ACTIVITY_TYPES.includes(type as TaskTypeEnum));
    return [...SALES_ACTIVITY_TYPES, ...extra].map((type) => ({ value: `${type}`, label: taskTypeLabelKey(`${type}`) }));
  }

  get showTypeError(): boolean {
    const control = this.form?.get('type');
    return !!control && control.touched && control.invalid;
  }

  static buildForm(formBuilder: FormBuilder, initial?: Partial<NextActivityFormValue>): FormGroup {
    return formBuilder.group({
      type: [initial?.type ?? TaskTypeEnum.CALL, Validators.required],
      name: [initial?.name ?? ''],
      moment: [initial?.moment ?? null, Validators.required],
      description: [initial?.description ?? ''],
    });
  }

  /** What the API receives (`POST /api/card/:id/next-action`, the `next` of the conclusion): the moment as an ISO instant. */
  static toPayload(value: NextActivityFormValue): NextActionPayload {
    return {
      type: value?.type,
      name: value?.name?.trim() || undefined,
      description: value?.description?.trim() || undefined,
      dueAt: new Date(value?.moment?.dueAt).toISOString(),
      untimed: value?.moment?.untimed === true,
    };
  }
}
